import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import api, { logoutClient } from './api';
import Spinner from './Spinner';
import HamburgerMenu from './HamburgerMenu';
import CustomAlert from './CustomAlert';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        background: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#212529',
        headerBg: isDark ? '#2a2a2a' : '#f8f9fa',
        border: isDark ? '#444' : '#e2e8f0',
        link: isDark ? '#6cb2f7' : '#007bff',
        buttonText: isDark ? '#121212' : '#fff',
        menuItemBg: isDark ? '#333' : '#fff',
        menuItemColor: isDark ? '#e0e0e0' : '#333',
        containerBg: isDark ? '#121212' : '#f4f6f8',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        inputBg: isDark ? '#333' : '#fff',
        inputBorder: isDark ? '#555' : '#ddd',
        inputText: isDark ? '#e0e0e0' : '#212529',
        success: isDark ? '#4caf50' : '#28a745',
        warning: isDark ? '#ff9800' : '#ffc107',
        danger: isDark ? '#f44336' : '#dc3545',
        info: isDark ? '#2196f3' : '#17a2b8',
        secondary: isDark ? '#9e9e9e' : '#64748b',
    };

    return {
        container: { 
            width: '95%', 
            maxWidth: '1400px', 
            margin: '20px auto', 
            fontFamily: 'system-ui, -apple-system, sans-serif', 
            color: colors.text, 
            backgroundColor: colors.containerBg 
        },
        header: { 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '30px', 
            flexWrap: 'wrap', 
            gap: '10px' 
        },
        pageTitle: { 
            fontSize: '28px', 
            fontWeight: 'bold', 
            margin: 0, 
            color: colors.text 
        },
        themeToggleButton: {
            padding: '8px 12px',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            backgroundColor: colors.background,
            color: colors.text,
            cursor: 'pointer',
        },
        tabContainer: {
            display: 'flex',
            marginBottom: '20px',
            borderBottom: `2px solid ${colors.border}`,
        },
        tab: {
            padding: '12px 24px',
            cursor: 'pointer',
            borderBottom: '2px solid transparent',
            backgroundColor: 'transparent',
            border: 'none',
            fontSize: '16px',
            fontWeight: '500',
            color: colors.text,
            transition: 'all 0.2s ease',
        },
        activeTab: {
            borderBottomColor: colors.link,
            color: colors.link,
            fontWeight: 'bold',
        },
        tabContent: {
            backgroundColor: colors.cardBg,
            borderRadius: '12px',
            padding: '24px',
            border: `1px solid ${colors.border}`,
            boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 12px rgba(0,0,0,0.06)',
        },
        // Create Inspection Form Styles
        form: { 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '20px', 
            alignItems: 'flex-end' 
        },
        formGroup: { 
            display: 'flex', 
            flexDirection: 'column' 
        },
        label: { 
            marginBottom: '8px', 
            fontWeight: 'bold', 
            color: colors.text 
        },
        input: { 
            padding: '12px', 
            borderRadius: '6px', 
            border: `1px solid ${colors.inputBorder}`, 
            backgroundColor: colors.inputBg, 
            color: colors.inputText, 
            fontSize: '16px',
            transition: 'border-color 0.2s ease',
        },
        button: { 
            padding: '12px 20px', 
            backgroundColor: colors.link, 
            color: colors.buttonText, 
            border: 'none', 
            borderRadius: '6px', 
            cursor: 'pointer', 
            fontSize: '16px', 
            fontWeight: 'bold',
            transition: 'background-color 0.2s ease',
        },
        buttonDisabled: { 
            padding: '12px 20px', 
            backgroundColor: '#ccc', 
            color: '#666', 
            border: 'none', 
            borderRadius: '6px', 
            cursor: 'not-allowed', 
            fontSize: '16px', 
            fontWeight: 'bold' 
        },
        // Inspection List Styles
        table: { 
            width: '100%', 
            borderCollapse: 'collapse', 
            marginTop: '20px',
            fontSize: '14px',
        },
        th: { 
            borderBottom: `2px solid ${colors.border}`, 
            padding: '14px 16px', 
            textAlign: 'left', 
            backgroundColor: colors.headerBg,
            fontWeight: '600',
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: colors.secondary,
        },
        td: { 
            borderBottom: `1px solid ${colors.border}`, 
            padding: '14px 16px',
            color: colors.text,
        },
        tableRow: {
            transition: 'background-color 0.15s ease',
        },
        tableRowHover: {
            backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        },
        actionLink: { 
            display: 'inline-block', 
            marginRight: '12px',
            marginBottom: '4px', 
            color: colors.link, 
            textDecoration: 'none', 
            fontWeight: '500',
            fontSize: '13px',
        },
        progressBarContainer: { 
            position: 'relative', 
            width: '100%', 
            minWidth: '80px',
            backgroundColor: isDark ? '#333' : '#e9ecef', 
            borderRadius: '6px', 
            overflow: 'hidden', 
            height: '20px' 
        },
        progressBar: { 
            height: '100%', 
            borderRadius: '6px',
            transition: 'width 0.3s ease-in-out' 
        },
        progressText: { 
            position: 'absolute', 
            width: '100%', 
            textAlign: 'center', 
            lineHeight: '20px', 
            fontSize: '12px',
            color: isDark ? '#fff' : '#333', 
            fontWeight: '600' 
        },
        completedBadge: { padding: '2px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' },
        remainingBadge: { padding: '2px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' },
        progressCell: { minWidth: '170px' },
        progressSummary: { display: 'flex', flexDirection: 'column', gap: '6px' },
        progressFraction: { fontSize: '14px', fontWeight: '600', color: colors.text },
        progressRemaining: { fontSize: '12px', color: colors.secondary },
        filtersContainer: { 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'flex-end', 
            padding: '18px 20px', 
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '10px', 
            flexWrap: 'wrap',
            marginBottom: '20px',
        },
        filterGroup: { 
            display: 'flex', 
            flexDirection: 'column' 
        },
        filterLabel: { 
            marginBottom: '5px', 
            fontSize: '14px', 
            fontWeight: 'bold',
            color: colors.text,
        },
        filterInput: { 
            padding: '8px', 
            borderRadius: '4px', 
            border: `1px solid ${colors.border}`, 
            backgroundColor: colors.background, 
            color: colors.text 
        },
        createFormContainer: {
            backgroundColor: colors.headerBg,
            padding: '20px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            marginBottom: '20px',
        },
        summaryStats: {
            display: 'flex',
            gap: '20px',
            flexWrap: 'wrap',
            marginBottom: '20px',
        },
        summaryStatCard: {
            padding: '16px 20px',
            borderRadius: '10px',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.cardBg,
            minWidth: '140px',
        },
        summaryStatValue: { fontSize: '24px', fontWeight: '700', color: colors.text },
        summaryStatLabel: { fontSize: '12px', color: colors.secondary, marginTop: '4px', fontWeight: '500' },
        createFormTitle: {
            fontSize: '20px',
            fontWeight: 'bold',
            marginBottom: '20px',
            color: colors.text,
        },
        menuItem: {
            padding: '10px 15px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'block',
            textDecoration: 'none',
            color: colors.menuItemColor,
        },
        menuItemButton: {
            background: 'none',
            border: 'none',
            padding: '10px 15px',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '16px',
            fontFamily: 'sans-serif',
            color: colors.menuItemColor,
        },
        success: colors.success,
        info: colors.info,
        warning: colors.warning,
        secondary: colors.secondary,
        link: colors.link,
        border: colors.border,
        notesInput: {
            width: '100%', minWidth: '180px', maxWidth: '280px',
            padding: '8px 10px', fontSize: '13px',
            border: `1px solid ${colors.inputBorder}`, borderRadius: '6px',
            backgroundColor: colors.inputBg, color: colors.inputText,
            resize: 'vertical', minHeight: '36px',
        },
        statusDetail: {
            fontSize: '11px',
            color: colors.secondary,
            lineHeight: 1.4,
        },
    };
};

function formatLastEditDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

function UnifiedInspectionPage({ theme, toggleTheme }) {
    const styles = getStyles(theme);
    const [inspections, setInspections] = useState([]);
    const [sites, setSites] = useState([]);
    const [tmsOptions, setTmsOptions] = useState([]);
    const [workBays, setWorkBays] = useState([]);
    const [checklistTypes, setChecklistTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createLoading, setCreateLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });

    const [filters, setFilters] = useState({ status: '', site: '', tms: '' });
    const [searchTerm, setSearchTerm] = useState('');

    // Create inspection form data
    const [formData, setFormData] = useState({
        site: '',
        tms: '',
        work_bay: '',
        buno: '',
        checklist_type: '',
    });

    const navigate = useNavigate();
    const { isStaff, groups, user } = useAuth();

    const canCreateInspection = groups.includes('Inspector') || groups.includes('Supervisor');

    // Filter sites based on user groups
    const filteredSites = React.useMemo(() => {
        if (isStaff) {
            return sites;
        }
        return sites.filter(site => {
            return groups.some(group => 
                group.toUpperCase() === site.name.toUpperCase()
            );
        });
    }, [sites, groups, isStaff]);

    // Filter inspections based on user's accessible sites
    const filteredInspections = React.useMemo(() => {
        if (isStaff) {
            return inspections;
        }
        const accessibleSiteNames = filteredSites.map(site => site.name);
        return inspections.filter(inspection => 
            accessibleSiteNames.includes(inspection.site)
        );
    }, [inspections, filteredSites, isStaff]);

    const handleLogout = useCallback(() => {
        logoutClient();
        navigate('/login');
    }, [navigate]);

    // Fetch inspections with filters
    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (filters.status) params.append('status', filters.status);
        if (filters.site) params.append('site', filters.site);
        if (filters.tms) params.append('tms', filters.tms);
        if (searchTerm) params.append('search', searchTerm);

        api.get(`/inspections/?${params.toString()}`)
            .then(res => {
                let inspectionData = res.data;
                if (res.data.results) {
                    inspectionData = res.data.results;
                }
                setInspections(inspectionData);
            })
            .catch(err => {
                console.error("Error fetching inspections:", err);
                if (err.response && err.response.status === 401) handleLogout();
                setAlert({ show: true, message: 'Failed to fetch inspections.', type: 'error' });
            })
            .finally(() => setLoading(false));
    }, [filters, searchTerm, handleLogout]);

    // Fetch dropdown data for create form
    useEffect(() => {
        Promise.all([
            api.get('/sites/'),
            api.get('/tms/'),
            api.get('/work-bays/'),
            api.get('/checklist-types/')
        ])
        .then(([sitesRes, tmsRes, workBaysRes, checklistTypesRes]) => {
            const sitesData = Array.isArray(sitesRes.data) ? sitesRes.data : [];
            const tmsData = Array.isArray(tmsRes.data) ? tmsRes.data : [];
            const workBaysData = Array.isArray(workBaysRes.data) ? workBaysRes.data : [];
            const checklistTypesData = Array.isArray(checklistTypesRes.data) ? checklistTypesRes.data : [];
            
            setSites(sitesData);
            setTmsOptions(tmsData);
            setWorkBays(workBaysData);
            setChecklistTypes(checklistTypesData);
        })
        .catch(err => {
            console.error("Error fetching dropdown data:", err);
            setAlert({ show: true, message: 'Failed to load form data.', type: 'error' });
        });
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };
    
    const handleResetFilters = () => {
        setFilters({ status: '', site: '', tms: '' });
        setSearchTerm('');
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    const handleCreateSubmit = (e) => {
        e.preventDefault();
        setCreateLoading(true);
        
        api.post('/inspections/', formData)
            .then(response => {
                setAlert({ show: true, message: 'Inspection created successfully!', type: 'success' });
                setFormData({ site: '', tms: '', work_bay: '', buno: '', checklist_type: '' });
                // Refresh inspections list
                const params = new URLSearchParams();
                if (filters.status) params.append('status', filters.status);
                if (filters.site) params.append('site', filters.site);
                if (filters.tms) params.append('tms', filters.tms);
                if (searchTerm) params.append('search', searchTerm);
                
                api.get(`/inspections/?${params.toString()}`)
                    .then(res => {
                        let inspectionData = res.data;
                        if (res.data.results) {
                            inspectionData = res.data.results;
                        }
                        setInspections(inspectionData);
                    })
                    .catch(err => console.error("Error refreshing inspections:", err));
            })
            .catch(error => {
                console.error("Error creating inspection:", error);
                const errorMessage = error.response?.data?.error || 'Failed to create inspection.';
                setAlert({ show: true, message: errorMessage, type: 'error' });
            })
            .finally(() => setCreateLoading(false));
    };

    const isFormValid = formData.site && formData.tms && formData.work_bay && formData.buno && formData.checklist_type;

    const handleNotesUpdate = (inspectionId, notes) => {
        api.patch(`/inspections/${inspectionId}/`, { notes })
            .then(res => {
                setInspections(prev => prev.map(i => i.id === inspectionId ? {
                    ...i,
                    notes: res.data.notes,
                    last_updated_by: res.data.last_updated_by || user?.username || i.last_updated_by,
                    updated_at: res.data.updated_at || new Date().toISOString(),
                } : i));
            })
            .catch(err => {
                console.error('Failed to update notes:', err);
                setAlert({ show: true, message: 'Failed to save notes.', type: 'error' });
            });
    };

    return (
        <div style={styles.container}>
            <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />
            
            <div style={styles.header}>
                <HamburgerMenu theme={theme}/>
                <h1 style={styles.pageTitle}>Inspections</h1>
                <button onClick={toggleTheme} style={styles.themeToggleButton}>
                    {theme === 'light' ? '🌙 Dark' : '☀️ Light'}
                </button>
                <div style={{ width: '45px' }}></div>
            </div>

            {/* Combined Content */}
            <div style={styles.tabContent}>
                {/* Create New Inspection Section */}
                {canCreateInspection && (
                    <div>
                        <h2 style={{ marginTop: 0, marginBottom: '20px', color: styles.container.color }}>
                            Create New Inspection
                        </h2>
                        
                        <form onSubmit={handleCreateSubmit} style={styles.form}>
                            <div style={styles.formGroup}>
                                <label style={styles.label} htmlFor="checklist_type">
                                    Inspection Type *
                                </label>
                                <select 
                                    id="checklist_type" 
                                    name="checklist_type" 
                                    value={formData.checklist_type} 
                                    onChange={handleFormChange} 
                                    style={styles.input} 
                                    required
                                >
                                    <option value="">Select an Inspection Type</option>
                                    {Array.isArray(checklistTypes) && checklistTypes.map(type => 
                                        <option key={type.id} value={type.id}>{type.name}</option>
                                    )}
                                </select>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label} htmlFor="site">
                                    Site *
                                </label>
                                <select 
                                    id="site" 
                                    name="site" 
                                    value={formData.site} 
                                    onChange={handleFormChange} 
                                    style={styles.input} 
                                    required
                                >
                                    <option value="">Select a Site</option>
                                    {Array.isArray(filteredSites) && filteredSites.map(site => 
                                        <option key={site.id} value={site.id}>{site.name}</option>
                                    )}
                                </select>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label} htmlFor="tms">
                                    TMS (Type Model Series) *
                                </label>
                                <select 
                                    id="tms" 
                                    name="tms" 
                                    value={formData.tms} 
                                    onChange={handleFormChange} 
                                    style={styles.input} 
                                    required
                                >
                                    <option value="">Select a TMS</option>
                                    {Array.isArray(tmsOptions) && tmsOptions.map(t => 
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    )}
                                </select>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label} htmlFor="work_bay">
                                    Work Bay *
                                </label>
                                <select 
                                    id="work_bay" 
                                    name="work_bay" 
                                    value={formData.work_bay} 
                                    onChange={handleFormChange} 
                                    style={styles.input} 
                                    required
                                >
                                    <option value="">Select a Work Bay</option>
                                    {Array.isArray(workBays) && workBays.map(bay => 
                                        <option key={bay.id} value={bay.id}>{bay.name}</option>
                                    )}
                                </select>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label} htmlFor="buno">BUNO *</label>
                                <input 
                                    id="buno" 
                                    type="text" 
                                    name="buno" 
                                    value={formData.buno} 
                                    onChange={handleFormChange} 
                                    style={styles.input} 
                                    placeholder="Enter aircraft BUNO"
                                    required 
                                />
                            </div>
                            
                            <button 
                                type="submit" 
                                style={createLoading || !isFormValid ? styles.buttonDisabled : styles.button}
                                disabled={createLoading || !isFormValid}
                            >
                                {createLoading ? 'Creating...' : 'Create Inspection'}
                            </button>
                        </form>
                    </div>
                )}

                {!canCreateInspection && (
                    <div style={{ textAlign: 'center', padding: '20px', marginBottom: '20px' }}>
                        <h3 style={{ color: styles.container.color }}>View-Only Access</h3>
                        <p style={{ color: styles.container.color }}>
                            {groups.includes('Viewer') 
                                ? 'You have view-only access to inspections. Contact your administrator to get Inspector or Supervisor access for editing capabilities.'
                                : 'You don\'t have permission to create inspections. Contact your administrator to get Inspector or Supervisor access.'
                            }
                        </p>
                    </div>
                )}

                {/* Inspections Dashboard Section */}
                <div>
                    <h2 style={{ marginTop: '30px', marginBottom: '20px', color: styles.container.color, fontSize: '22px', fontWeight: '600' }}>
                        Inspections Dashboard
                    </h2>
                    
                    {/* Summary Stats */}
                    {!loading && filteredInspections.length > 0 && (() => {
                        const total = filteredInspections.length;
                        const inProgress = filteredInspections.filter(i => i.status === 'In Progress').length;
                        const completed = filteredInspections.filter(i => i.status === 'Completed').length;
                        const totalItems = filteredInspections.reduce((sum, i) => sum + (i.total_items || 0), 0);
                        const completedItems = filteredInspections.reduce((sum, i) => sum + (i.completed_items || 0), 0);
                        const remainingItems = totalItems - completedItems;
                        const totalCards = filteredInspections.reduce((sum, i) => sum + (i.total_cards || 0), 0);
                        const completedCards = filteredInspections.reduce((sum, i) => sum + (i.completed_cards || 0), 0);
                        const remainingCards = totalCards - completedCards;
                        return (
                            <div style={styles.summaryStats}>
                                <div style={styles.summaryStatCard}>
                                    <div style={styles.summaryStatValue}>{total}</div>
                                    <div style={styles.summaryStatLabel}>Inspections</div>
                                </div>
                                <div style={styles.summaryStatCard}>
                                    <div style={{ ...styles.summaryStatValue, color: styles.info }}>{inProgress}</div>
                                    <div style={styles.summaryStatLabel}>In Progress</div>
                                </div>
                                <div style={styles.summaryStatCard}>
                                    <div style={{ ...styles.summaryStatValue, color: styles.success }}>{completed}</div>
                                    <div style={styles.summaryStatLabel}>Completed</div>
                                </div>
                                <div style={styles.summaryStatCard}>
                                    <div style={{ ...styles.summaryStatValue, color: styles.success }}>{completedItems}</div>
                                    <div style={styles.summaryStatLabel}>Items Done</div>
                                </div>
                                <div style={styles.summaryStatCard}>
                                    <div style={{ ...styles.summaryStatValue, color: styles.warning }}>{remainingItems}</div>
                                    <div style={styles.summaryStatLabel}>Items Left</div>
                                </div>
                                <div style={styles.summaryStatCard}>
                                    <div style={{ ...styles.summaryStatValue, color: styles.success }}>{completedCards}</div>
                                    <div style={styles.summaryStatLabel}>Cards Done</div>
                                </div>
                                <div style={styles.summaryStatCard}>
                                    <div style={{ ...styles.summaryStatValue, color: styles.warning }}>{remainingCards}</div>
                                    <div style={styles.summaryStatLabel}>Cards Left</div>
                                </div>
                            </div>
                        );
                    })()}
                    
                    {/* Filters */}
                    <div style={styles.filtersContainer}>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel} htmlFor="search">Search BUNO</label>
                            <input
                                type="text"
                                id="search"
                                placeholder="Enter BUNO..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={styles.filterInput}
                            />
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel} htmlFor="status">Status</label>
                            <select id="status" name="status" value={filters.status} onChange={handleFilterChange} style={styles.filterInput}>
                                <option value="">All</option>
                                <option value="Not Started">Not Started</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Completed">Completed</option>
                            </select>
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel} htmlFor="site">Site</label>
                            <select id="site" name="site" value={filters.site} onChange={handleFilterChange} style={styles.filterInput}>
                                <option value="">All Sites</option>
                                {Array.isArray(filteredSites) && filteredSites.map(site => 
                                    <option key={site.id} value={site.id}>{site.name}</option>
                                )}
                            </select>
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel} htmlFor="tms">TMS</label>
                            <select id="tms" name="tms" value={filters.tms} onChange={handleFilterChange} style={styles.filterInput}>
                                <option value="">All TMS</option>
                                {Array.isArray(tmsOptions) && tmsOptions.map(tms => 
                                    <option key={tms.id} value={tms.id}>{tms.name}</option>
                                )}
                            </select>
                        </div>
                        <button onClick={handleResetFilters} style={{ alignSelf: 'flex-end', padding: '8px 12px' }}>
                            Reset
                        </button>
                    </div>

                    {/* Inspections Table */}
                    {loading ? <Spinner theme={theme} /> : (
                        <div style={{ overflowX: 'auto', borderRadius: '10px', border: `1px solid ${styles.border}` }}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>BUNO</th>
                                        <th style={styles.th}>Site</th>
                                        <th style={styles.th}>TMS</th>
                                        <th style={styles.th}>Inspection</th>
                                        <th style={{ ...styles.th, minWidth: '140px' }}>Status</th>
                                        <th style={{ ...styles.th, minWidth: '180px' }}>Progress</th>
                                        <th style={styles.th}>Start</th>
                                        <th style={styles.th}>ECD</th>
                                        <th style={{ ...styles.th, minWidth: '200px' }}>Notes</th>
                                        <th style={styles.th}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(filteredInspections) && filteredInspections.map(insp => {
                                        const total = insp.total_items ?? 0;
                                        const completed = insp.completed_items ?? 0;
                                        const remaining = total - completed;
                                        const pct = insp.percent_complete ?? 0;
                                        return (
                                            <tr key={insp.id} style={styles.tableRow}>
                                                <td style={styles.td}><strong>{insp.buno}</strong></td>
                                                <td style={styles.td}>{insp.site}</td>
                                                <td style={styles.td}>{insp.tms}</td>
                                                <td style={styles.td}>{insp.checklist_type}</td>
                                                <td style={styles.td}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                        <span style={{
                                                            ...styles.completedBadge,
                                                            backgroundColor: insp.status === 'Completed' ? 'rgba(40, 167, 69, 0.15)' : insp.status === 'In Progress' ? 'rgba(23, 162, 184, 0.15)' : 'rgba(108, 117, 125, 0.15)',
                                                            color: insp.status === 'Completed' ? styles.success : insp.status === 'In Progress' ? styles.info : styles.secondary
                                                        }}>
                                                            {insp.status}
                                                        </span>
                                                        <span style={styles.statusDetail}>
                                                            Last edit: {insp.last_updated_by || '—'}
                                                            {insp.updated_at && (
                                                                <> · {formatLastEditDate(insp.updated_at)}</>
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td style={{ ...styles.td, ...styles.progressCell }}>
                                                    <div style={styles.progressSummary}>
                                                        <div style={styles.progressFraction}>
                                                            <span style={{ color: styles.success }}>{completed}</span>
                                                            <span style={{ color: styles.secondary }}> / </span>
                                                            <span>{total}</span>
                                                            <span style={{ color: styles.secondary, fontWeight: '500', marginLeft: '4px' }}>items</span>
                                                        </div>
                                                        {(insp.total_cards != null && insp.completed_cards != null) && (
                                                            <div style={{ fontSize: '12px', color: styles.secondary }}>
                                                                <span style={{ color: styles.success }}>{insp.completed_cards}</span>
                                                                <span> / </span>
                                                                <span>{insp.total_cards}</span>
                                                                <span style={{ marginLeft: '4px' }}>cards</span>
                                                            </div>
                                                        )}
                                                        <div style={styles.progressRemaining}>
                                                            {remaining > 0 ? (
                                                                <span style={{ color: styles.warning }}>{remaining} items remaining</span>
                                                            ) : (
                                                                <span style={{ color: styles.success }}>All complete</span>
                                                            )}
                                                        </div>
                                                        <div style={styles.progressBarContainer}>
                                                            <div style={styles.progressText}>{pct}%</div>
                                                            <div style={{
                                                                ...styles.progressBar,
                                                                width: `${Math.min(pct, 100)}%`,
                                                                backgroundColor: pct === 100 ? styles.success : styles.link
                                                            }}></div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={styles.td}>{insp.start_date ? new Date(insp.start_date).toLocaleDateString() : '—'}</td>
                                                <td style={styles.td}>{insp.ecd ? new Date(insp.ecd).toLocaleDateString() : '—'}</td>
                                                <td style={styles.td}>
                                                    <textarea
                                                        placeholder="e.g., Awaiting parts, stopped work..."
                                                        defaultValue={insp.notes || ''}
                                                        disabled={!canCreateInspection}
                                                        onBlur={(e) => {
                                                            const val = e.target.value.trim();
                                                            if (val !== (insp.notes || '')) handleNotesUpdate(insp.id, val);
                                                        }}
                                                        style={styles.notesInput}
                                                        title="Add notes about this inspection"
                                                    />
                                                </td>
                                                <td style={styles.td}>
                                                    {insp.status === 'Completed' ? (
                                                        <Link to={`/inspection/${insp.id}/report`} style={styles.actionLink}>
                                                            View Report
                                                        </Link>
                                                    ) : insp.status === 'In Progress' ? (
                                                        <>
                                                            <Link to={`/inspection/${insp.id}`} style={styles.actionLink}>
                                                                Continue
                                                            </Link>
                                                            <Link to={`/inspection/${insp.id}/progress-report`} style={styles.actionLink}>
                                                                Progress
                                                            </Link>
                                                            <Link to={`/inspection/${insp.id}/burndown`} style={styles.actionLink}>
                                                                Chart
                                                            </Link>
                                                        </>
                                                    ) : (
                                                        <Link to={`/inspection/${insp.id}`} style={styles.actionLink}>
                                                            Start
                                                        </Link>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default UnifiedInspectionPage;